/**
 * Custom Hook: useOtList
 * Spec: 003-optimized-ot-list
 * 
 * Hook principal que consolida toda la lógica de gestión de OTs:
 * - Paginación
 * - Filtros
 * - Caché con localStorage (TTL 20min)
 * - Invalidación automática vía Socket.io
 * - Loading states
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useOtCache } from './useOtCache';
import { paginate } from '../api/paginate';
import { PARENT } from '../constants';

/**
 * Hook para gestión completa de la lista de OTs
 * @param {Object} options - Configuración opcional
 * @param {number} options.initialPerPage - Items por página inicial (default: 20)
 * @param {Object} options.initialFilters - Filtros iniciales
 * @returns {Object} Estado y funciones de control
 */
export const useOtList = ({ initialPerPage = 20, initialFilters = {} } = {}) => {
    // Funcion que invalida la cache, debe ir antes de cualquier uso
    const invalidateCache = () => {
      if (typeof window !== 'undefined') {
        const userId = userData?._id;
        if (userId) localStorage.removeItem(`ot_list_cache_${userId}`);
      }
    };
  const { userData } = useAuth();

  const { getCached, setCached, pendingUpdates, clearPendingUpdates } = useOtCache();

  // Clave para persistencia de UI por usuario
  const STORAGE_KEY = userData?._id ? `ot_list_ui_${userData._id}` : null;

  // Inicializador robusto para restaurar estado desde localStorage
  const getInitialState = () => {
    if (typeof window !== 'undefined' && STORAGE_KEY) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const { page: savedPage, perPage: savedPerPage, filters: savedFilters } = JSON.parse(saved);
          return {
            page: savedPage || 1,
            perPage: savedPerPage || initialPerPage,
            filters: savedFilters || initialFilters
          };
        } catch {}
      }
    }
    return {
      page: 1,
      perPage: initialPerPage,
      filters: initialFilters
    };
  };

  const { page: initialPage, perPage: initialPerPageState, filters: initialFiltersState } = getInitialState();

  // Estados consolidados (reemplaza 14 useState en Ot.js)
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPageState);
  const [filters, setFilters] = useState(initialFiltersState);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // (Ya no es necesario restaurar con useEffect, se hace en la inicialización)

  // Guardar estado de UI en localStorage al cambiar page, perPage o filters
  useEffect(() => {
    if (STORAGE_KEY) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ page, perPage, filters }));
    }
  }, [page, perPage, filters, STORAGE_KEY]);

  // Utilidad para pasar la combinación actual a getCached/setCached
  const getCacheParams = useCallback(() => ({ page, perPage, filters }), [page, perPage, filters]);

  /**
   * Construir condiciones de consulta
   * Elimina filtros con valores vacíos para no contaminar la query
   * Transforma filtros de fecha (fechaDesde/fechaHasta) a queries sobre createdAt
   */
  const buildConditions = useCallback(() => {
    const { fechaDesde, fechaHasta, ...otherFilters } = filters;
    
    // Limpiar filtros regulares (excepto fechas)
    const cleanFilters = Object.fromEntries(
      Object.entries(otherFilters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );

    // Construir condiciones base
    const conditions = {
      otType: PARENT,
      $or: [{ activated: true }, { activated: null }],
      ...cleanFilters
    };

    // Agregar filtro de rango de fechas sobre createdAt
    if (fechaDesde || fechaHasta) {
      conditions.createdAt = {};
      
      if (fechaDesde) {
        // Fecha desde: inicio del día (00:00:00)
        const startDate = new Date(fechaDesde);
        startDate.setHours(0, 0, 0, 0);
        conditions.createdAt.$gte = startDate.toISOString();
      }
      
      if (fechaHasta) {
        // Fecha hasta: fin del día (23:59:59.999)
        const endDate = new Date(fechaHasta);
        endDate.setHours(23, 59, 59, 999);
        conditions.createdAt.$lte = endDate.toISOString();
      }
    }

    return conditions;
  }, [filters]);

  /**
   * Cargar datos de OTs (con caché)
   */
  const loadOtList = useCallback(async () => {
    if (!userData) return;

    setLoading(true);
    setError(null);

    try {
      const conditions = buildConditions();
      // Usar cualquier dato en caché si existe
      const cached = getCached();

      console.log('cargado cache:', cached);

      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }

      // Si no hay caché, consultar API

      console.log('Consultando API con condiciones:', conditions, 'page:', page, 'perPage:', perPage);
      const response = await paginate(
        `/Ot/paginar?page=${page}&perPage=${perPage}`,
        {
          conditions,
          sort: { _id: -1 }
        }
      );
      
      if (response?.data?.data) {
        const rawData = response.data.data;
        // Mapear estructura del backend (snake_case) a estructura esperada (camelCase)
        const otData = {
          results: rawData.results || [],
          page: rawData.page || page,
          perPage: rawData.perPage || perPage,
          total: rawData.total_elements || 0,
          totalPages: rawData.total_pages || 0,
          hasNextPage: !!rawData.next,
          hasPrevPage: !!rawData.prev,
          next: rawData.next,
          prev: rawData.prev
        };
        setData(otData);
        // Guardar en caché (sin filtros/paginado)
        setCached(otData);

        
      } else {
        throw new Error('Respuesta inválida de la API');
      }
    } catch (err) {
      setError(err.message || 'Error cargando lista de OTs');
    } finally {
      setLoading(false);
    }
  }, [userData, buildConditions, getCached, setCached, page, perPage]);


  /**
   * Efecto para cargar datos cuando cambian dependencias
   */
  useEffect(() => {
    loadOtList();
  }, [loadOtList, refreshTrigger, page, perPage]);

  /**
   * Forzar recarga de datos (bypass caché)
   */
  const refresh = useCallback(() => {
    invalidateCache(); // Invalida caché antes de recargar
    setRefreshTrigger(prev => prev + 1);
    clearPendingUpdates();
  }, [clearPendingUpdates]);

  /**
   * Cambiar página
   */
  const changePage = useCallback((newPage) => {
    invalidateCache(); // Invalida caché al cambiar de página
    setData({}); // Limpiar datos mientras carga
    setPage(newPage);
  }, []);

  /**
   * Actualizar filtros (reinicia a página 1)
   */
  const updateFilters = useCallback((newFilters) => {
    setPage(1);
    // Invalidar caché al cambiar filtros
    invalidateCache()
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  /**
   * Actualizar un solo filtro
   */
  const updateFilter = useCallback((name, value) => {
    setPage(1);
    // Invalidar caché al cambiar un filtro
    invalidateCache();
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  }, []);

  /**
   * Reiniciar filtros
   */
  const resetFilters = useCallback(() => {
    setPage(1);
    // Invalidar caché al resetear filtros
    invalidateCache();
    
    setFilters(initialFilters);
  }, [initialFilters]);

  /**
   * Reiniciar a página 1 manteniendo filtros
   */
  const resetPage = useCallback(() => {
    //invalida caché con la funcion invalidateCache() cada vez que se cambia de página
    invalidateCache();
    setPage(1);
  }, []);

  /**
   * Cambiar cantidad de items por página (vuelve a página 1)
   */
  const changePerPage = useCallback((newPerPage) => {
    // invalido el caché al cambiar paginado utiliza la funcion para invalidar cache
    invalidateCache();
    setPage(1);
    setPerPage(newPerPage);
  }, [invalidateCache]);

  // Invalidar caché automaticamente al cambiar filtros


  // Retornar API pública del hook
  return {
    // Datos
    data,
    results: data.results || [], // Array de OTs
    pagination: {
      page: data.page || 1,
      perPage: data.perPage || perPage,
      total: data.total || 0,
      totalPages: data.totalPages || 0,
      hasNextPage: data.hasNextPage || false,
      hasPrevPage: data.hasPrevPage || false
    },
    
    // Estados
    loading,
    error,
    
    // Filtros
    filters,
    updateFilters,
    updateFilter,
    resetFilters,
    
    // Paginación
    page,
    changePage,
    resetPage,
    perPage,
    changePerPage,
    
    // Acciones
    refresh,
    
    // Indicadores de caché
    pendingUpdates, // Número de actualizaciones pendientes desde Socket.io
    hasPendingUpdates: pendingUpdates > 0
  };
};
